import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE, scrollToId } from "../data/projects";

const LINES = [
  { text: "BUILD TODAY", accent: false },
  { text: "FOR TOMORROW.", accent: true },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
};

const lineVariant = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Modern illuminated architectural home exterior renovated by Joba Construction"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
        <div className="hero-glow absolute inset-0" />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pt-32 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-copper"
          data-testid="hero-eyebrow"
        >
          Architectural Renovation — Philadelphia, PA
        </motion.p>

        <motion.h1
          data-testid="hero-heading"
          variants={container}
          initial="hidden"
          animate="show"
          className="mt-6 font-display text-[13.5vw] font-bold leading-[0.92] tracking-tight text-cream sm:text-7xl lg:text-8xl"
        >
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                variants={lineVariant}
                className={`block ${l.accent ? "text-copper" : ""}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-cream/70"
            data-testid="hero-subtext"
          >
            The most innovative structures — Joba Construction is a
            renovation atelier for kitchens, spa bathrooms, outdoor decks and
            artisan wall finishes, engineered with architectural rigor and
            delivered with white-glove care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-2 rounded-full bg-copper px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-copper-hover"
            >
              Start your project
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("portfolio")}
              className="rounded-full border border-cream/25 px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:border-copper hover:text-copper"
            >
              View portfolio
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="relative z-10 mx-auto flex w-full max-w-7xl items-end justify-between px-4 pb-10 pt-16 sm:px-8"
      >
        <div
          data-testid="hero-badge"
          className="rounded-2xl border border-cream/15 bg-ink/50 px-5 py-4 backdrop-blur-md"
        >
          <p className="font-display text-3xl font-semibold text-copper">142</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
            Awarded remodels
          </p>
        </div>
        <button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToId("services")}
          className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/50 transition-colors hover:text-copper"
        >
          Scroll
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20">
            <ArrowDown size={14} className="animate-bounce" />
          </span>
        </button>
      </motion.div>
    </section>
  );
}
